import { useParams } from 'react-router';

import z from 'zod';

import { PlanUsersReport, UserPlans } from '@cfa/pathway-fe-shared/xp';

import { getProgressBarText } from '@/utils/generateProgressBarText';
import { itemByLanguage } from '@/utils/language';
import { useRouteMatcher } from '@/utils/url';

import Constants, { routes } from '@/constants';

import type { TeamMemberRow } from './teamMemberRow';

type RouteMatch =
  (typeof Constants.ROUTE_PATH_GROUPS)[keyof typeof Constants.ROUTE_PATH_GROUPS];

export interface UseTeamMemberCardParams {
  member: TeamMemberRow;
  plan?: UserPlans;
  planReport?: PlanUsersReport;
  reportsRoute?: boolean;
}

export interface TeamMemberCardRouteFlags {
  isReportsTeamMembersPath: boolean;
  isReportsPlansViewPath: boolean;
  isTrainingTeamMembersPath: boolean;
}

export const getMemberCompletionFromStatus = (status?: string) => ({
  isComplete:
    status === Constants.TRAINING_PLANS.COMPLETED ||
    status === Constants.LEARN_UPON_TRAINING_PLANS.COMPLETED ||
    status === Constants.LEARN_UPON_TRAINING_PLANS.PASSED,
  isFailed: status === Constants.LEARN_UPON_TRAINING_PLANS.FAILED,
});

export const getSelectedDueDate = (dueDate?: string | null) =>
  new Date(dueDate ?? Infinity).getTime();

export const getTeamMemberPlanContext = (
  plan?: UserPlans,
  planReport?: PlanUsersReport,
) => {
  const planEntity = plan?.plan;
  const planType = planEntity?.type ?? planReport?.type;
  const isCompliance = planType === 'COMPLIANCE' || planType === 'ANCHOR';

  const stepCountFromPlan =
    planEntity?.totalSteps ?? planReport?.totalSteps ?? 0;

  const planName = itemByLanguage(planEntity?.name ?? planReport?.name);

  const userAllowedToUpdateTMDueDateOnPlan =
    z
      .object({
        updateDueDate: z.boolean(),
      })
      .safeParse(planEntity?.permissions).data?.updateDueDate ?? false;

  const userCanMarkAsComplete =
    planEntity?.permissions?.markAllStepsAndPlanComplete ?? true;

  return {
    isCompliance,
    stepCountFromPlan,
    planName,
    complianceDueDateOffset: plan?.plan?.dueDateOffsetInDays,
    userAllowedToUpdateTMDueDateOnPlan,
    userCanMarkAsComplete,
  };
};

export const getTeamMemberCardHref = ({
  id,
  planId,
  routeFlags,
}: {
  id: string;
  planId?: string;
  routeFlags: TeamMemberCardRouteFlags;
}) => {
  const {
    isReportsTeamMembersPath,
    isReportsPlansViewPath,
    isTrainingTeamMembersPath,
  } = routeFlags;

  if (isReportsTeamMembersPath) {
    return routes.reports.teamMemberProgress({ teamMemberId: id });
  }
  if (isReportsPlansViewPath && planId) {
    return routes.reports.teamMemberPlanProgress({
      planId,
      teamMemberId: id,
    });
  }
  if (isTrainingTeamMembersPath && planId) {
    return routes.plans.managePlanTeamMembers({
      teamMemberId: id,
      planId,
    });
  }
  return '/';
};

export const getTeamMemberProgressPercent = (
  completedStepCount: number,
  stepCount: number,
) =>
  completedStepCount
    ? Math.min(100, Number(((completedStepCount / stepCount) * 100).toFixed(2)))
    : 0;

export const getTeamMemberRouteFlags = (
  routeMatches: RouteMatch[],
): TeamMemberCardRouteFlags => ({
  isReportsPlansViewPath: routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.ReportsPlansViewPath,
  ),
  isReportsTeamMembersPath: routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.ReportsTeamMembersPath,
  ),
  isTrainingTeamMembersPath: routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.TrainingTeamMembersPath,
  ),
});

export const useTeamMemberCard = ({
  member,
  plan,
  planReport,
  reportsRoute,
}: UseTeamMemberCardParams) => {
  const routeMatches = useRouteMatcher();
  const { planId: routePlanId } = useParams<{ planId: string }>();
  const planId = routePlanId ?? undefined;

  const matchedRouteFlags = getTeamMemberRouteFlags(routeMatches);
  const routeFlags = {
    ...matchedRouteFlags,
    isReportsPlansViewPath:
      reportsRoute ?? matchedRouteFlags.isReportsPlansViewPath,
  };
  const isReports =
    routeFlags.isReportsPlansViewPath || routeFlags.isReportsTeamMembersPath;
  const isReportsPlansView = routeFlags.isReportsPlansViewPath;

  const planContext = getTeamMemberPlanContext(plan, planReport);
  const stepCount =
    (planContext.stepCountFromPlan > 0
      ? planContext.stepCountFromPlan
      : member.stepCount) ?? 0;

  const { isComplete: isCompleteFromStatus, isFailed } =
    getMemberCompletionFromStatus(member.status);
  const isComplete =
    member.status !== undefined && member.status !== ''
      ? isCompleteFromStatus
      : stepCount > 0 &&
        member.completedStepCount > 0 &&
        member.completedStepCount === stepCount;

  const selectedDueDate = getSelectedDueDate(member.dueDate);

  const href = getTeamMemberCardHref({
    id: member.id,
    planId,
    routeFlags,
  });

  const isCompleteAndReports = isComplete && isReports;
  const progressBarText = getProgressBarText({
    isReports,
    isComplete,
    completedStepCount: member.completedStepCount,
    stepCount,
    timeSpentOnPlan: member.timeSpentOnPlan,
    isCompliance: false,
    percentComplete: member.percentComplete,
  });

  const progressPercent = getTeamMemberProgressPercent(
    member.completedStepCount,
    stepCount,
  );

  const showProgress = !isCompleteAndReports && !isComplete && stepCount > 0;

  const title = member.displayName || member.name || '';

  return {
    title,
    displayName: member.displayName,
    href,
    isComplete,
    isFailed,
    isReports,
    isReportsPlansView,
    selectedDueDate,
    stepCount,
    progressBarText,
    progressPercent,
    showProgress,
    planId,
    planName: planContext.planName,
    isCompliance: planContext.isCompliance,
    complianceDueDateOffset: planContext.complianceDueDateOffset,
    userAllowedToUpdateTMDueDateOnPlan:
      planContext.userAllowedToUpdateTMDueDateOnPlan,
    userCanMarkAsComplete: planContext.userCanMarkAsComplete,
    hasPreviouslyCompletedPlanAssignment:
      !!member.previouslyCompletedPlanAssignmentDate,
    previouslyCompletedPlanAssignmentDate:
      member.previouslyCompletedPlanAssignmentDate,
    member,
  };
};
